import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../utils/api';
import {
  Plus, Search, Archive, ClipboardList, X, ChevronRight,
  Check, Trash2, Package,
} from 'lucide-react';
import toast from 'react-hot-toast';

const EVENT_TYPES = {
  veranstaltung:    { label: 'Veranstaltung',    emoji: '🎉' },
  schuetzenfest:    { label: 'Schützenfest',      emoji: '🎯' },
  toilettenanhaenger: { label: 'Toilettenanhänger', emoji: '🚽' },
  kuehlanhaenger:   { label: 'Kühlanhänger',      emoji: '❄️' },
  ausschankwagen:   { label: 'Ausschankwagen',    emoji: '🍺' },
  sonstiges:        { label: 'Sonstiges',          emoji: '📦' },
};

const STATUS = {
  offen:         { label: 'Offen',         badge: 'badge-red'    },
  in_bearbeitung:{ label: 'In Bearbeitung',badge: 'badge-yellow' },
  erledigt:      { label: 'Erledigt',      badge: 'badge-green'  },
};

// Vorlagen für die verschiedenen Event-Typen
const TEMPLATES = {
  veranstaltung: [
    { category:'Getränke-Equipment', name:'Zapfanlage',       quantity:1, unit:'Stück' },
    { category:'Getränke-Equipment', name:'CO2-Flasche',      quantity:2, unit:'Stück' },
    { category:'Getränke-Equipment', name:'Bierleitungen',    quantity:1, unit:'Set' },
    { category:'Geschirr',           name:'Gläser',           quantity:100, unit:'Stück' },
    { category:'Geschirr',           name:'Tabletts',         quantity:10, unit:'Stück' },
    { category:'Sonstiges',          name:'Verlängerungskabel',quantity:2, unit:'Stück' },
  ],
  schuetzenfest: [
    { category:'Getränke-Equipment', name:'Zapfanlage',       quantity:2, unit:'Stück' },
    { category:'Getränke-Equipment', name:'CO2-Flasche',      quantity:4, unit:'Stück' },
    { category:'Getränke-Equipment', name:'Bierleitungen',    quantity:2, unit:'Set' },
    { category:'Geschirr',           name:'Gläser',           quantity:500, unit:'Stück' },
    { category:'Geschirr',           name:'Tabletts',         quantity:20, unit:'Stück' },
    { category:'Zelt & Mobiliar',    name:'Stehtische',       quantity:10, unit:'Stück' },
    { category:'Zelt & Mobiliar',    name:'Bierbänke',        quantity:20, unit:'Stück' },
  ],
  toilettenanhaenger: [
    { category:'Verbrauchsmaterial', name:'Toilettenpapier',  quantity:50, unit:'Rollen' },
    { category:'Verbrauchsmaterial', name:'Seife',            quantity:5, unit:'Liter' },
    { category:'Verbrauchsmaterial', name:'Papierhandtücher', quantity:10, unit:'Rollen' },
    { category:'Reinigung',          name:'Reinigungsmittel', quantity:3, unit:'Liter' },
    { category:'Reinigung',          name:'Handschuhe',       quantity:2, unit:'Packungen' },
  ],
  kuehlanhaenger: [
    { category:'Betrieb',            name:'Stromanschluss prüfen', quantity:1, unit:'Check' },
    { category:'Betrieb',            name:'Temperatur einstellen', quantity:1, unit:'Check' },
    { category:'Zubehör',            name:'Ablagetücher',      quantity:5, unit:'Stück' },
  ],
  ausschankwagen: [
    { category:'Getränke-Equipment', name:'Zapfanlage',       quantity:1, unit:'Stück' },
    { category:'Getränke-Equipment', name:'CO2-Flasche',      quantity:2, unit:'Stück' },
    { category:'Geschirr',           name:'Gläser',           quantity:200, unit:'Stück' },
    { category:'Sonstiges',          name:'Kasse',            quantity:1, unit:'Stück' },
    { category:'Sonstiges',          name:'Wechselgeld',      quantity:1, unit:'Set' },
  ],
  sonstiges: [
    { category:'Allgemein',          name:'Checkliste',       quantity:1, unit:'Stück' },
  ],
};

const EMPTY_FORM = {
  title: '', event_type: 'veranstaltung', event_date: '',
  location: '', vehicle_id: '', notes: '', assigned_to: '',
  use_template: true,
};

function Modal({ title, onClose, children, footer }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,.5)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: 'var(--bg-card)',
        borderRadius: '20px 20px 0 0',
        width: '100%', maxWidth: 600,
        maxHeight: '94dvh',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        <div style={{ width:36, height:4, background:'var(--border)', borderRadius:2, margin:'10px auto 0', flexShrink:0 }}/>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 18px', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
          <span style={{ fontWeight:700, fontSize:'1rem' }}>{title}</span>
          <button onClick={onClose} style={{ display:'flex', alignItems:'center', padding:7, border:'none', background:'var(--bg)', cursor:'pointer', borderRadius:8, color:'var(--text)' }}>
            <X size={16}/>
          </button>
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:'16px 18px' }}>
          {children}
        </div>
        {footer && (
          <div style={{ padding:'14px 18px', borderTop:'1px solid var(--border)', display:'flex', gap:10, flexShrink:0 }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Packliste() {
  const nav = useNavigate();
  const [lists, setLists]       = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [archived, setArchived] = useState(false);
  const [search, setSearch]     = useState('');
  const [typeFilter, setType]   = useState('');
  const [modal, setModal]       = useState(false);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [saving, setSaving]     = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/packing', {
      params: {
        archived: archived ? 1 : 0,
        search:   search     || undefined,
        event_type: typeFilter || undefined,
      }
    })
      .then(r => setLists(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error('Fehler beim Laden'))
      .finally(() => setLoading(false));
  }, [archived, search, typeFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/vehicles').then(r => setVehicles(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/users').then(r => setUsers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Titel ist Pflicht'); return; }
    setSaving(true);
    try {
      // Vorlage-Items mitschicken wenn gewünscht
      const body = { ...form };
      if (form.use_template && TEMPLATES[form.event_type]) {
        body.custom_items = TEMPLATES[form.event_type];
      }
      const res = await api.post('/packing', body);
      toast.success('Packliste erstellt');
      setModal(false);
      setForm(EMPTY_FORM);
      load();
      // Direkt zur neuen Packliste navigieren
      if (res.data?.id) {
        setTimeout(() => nav(`/packliste/${res.data.id}`), 500);
      }
    } catch(err) {
      toast.error(err.response?.data?.error || 'Fehler beim Erstellen');
    } finally {
      setSaving(false);
    }
  };

  const deleteList = async (list, e) => {
    e.stopPropagation();
    if (!window.confirm(`"${list.title}" wirklich löschen?`)) return;
    try {
      await api.delete(`/packing/${list.id}`);
      toast.success('Gelöscht');
      load();
    } catch(err) {
      toast.error(err.response?.data?.error || 'Fehler');
    }
  };

  const archiveList = async (list, e) => {
    e.stopPropagation();
    try {
      await api.put(`/packing/${list.id}`, { archived: !list.archived });
      toast.success(list.archived ? 'Wiederhergestellt' : 'Archiviert');
      load();
    } catch { toast.error('Fehler'); }
  };

  const progress = (list) => {
    if (!list.totalItems) return 0;
    return Math.round((list.packedItems || 0) / list.totalItems * 100);
  };

  const templatePreview = TEMPLATES[form.event_type] || [];

  return (
    <Layout title="Packlisten">

      {/* Segment + Neu-Button */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14, flexWrap:'wrap', gap:10 }}>
        <div className="seg">
          <button className={`seg-btn ${!archived?'active':''}`} onClick={() => setArchived(false)}>
            <ClipboardList size={13}/> Aktiv
          </button>
          <button className={`seg-btn ${archived?'active':''}`} onClick={() => setArchived(true)}>
            <Archive size={13}/> Archiv
          </button>
        </div>
        <button className="btn btn-primary" onClick={() => { setForm(EMPTY_FORM); setModal(true); }}>
          <Plus size={14}/> Neue Packliste
        </button>
      </div>

      {/* Filter */}
      <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
        <div style={{ position:'relative', flex:'1 1 160px' }}>
          <Search size={13} style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', color:'var(--text-muted)' }}/>
          <input className="f-input" placeholder="Titel, Ort..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft:28 }}/>
        </div>
        <select className="f-input" style={{ width:175 }} value={typeFilter} onChange={e => setType(e.target.value)}>
          <option value="">Alle Typen</option>
          {Object.entries(EVENT_TYPES).map(([v, { label, emoji }]) => (
            <option key={v} value={v}>{emoji} {label}</option>
          ))}
        </select>
      </div>

      {/* Listen */}
      {loading ? (
        <div className="loading-box"><div className="spinner spinner-lg"/></div>
      ) : lists.length === 0 ? (
        <div className="card">
          <div className="empty-box">
            <ClipboardList size={40}/>
            <h3>{archived ? 'Keine archivierten Packlisten' : 'Noch keine Packlisten'}</h3>
            {!archived && (
              <button className="btn btn-primary" style={{ marginTop:14 }} onClick={() => { setForm(EMPTY_FORM); setModal(true); }}>
                <Plus size={14}/> Erste Packliste anlegen
              </button>
            )}
          </div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {lists.map(list => {
            const pct    = progress(list);
            const et     = EVENT_TYPES[list.event_type] || { emoji:'📦', label: list.event_type };
            const status = STATUS[list.status] || STATUS.offen;
            return (
              <div
                key={list.id}
                className="card"
                style={{ cursor:'pointer', transition:'transform 120ms' }}
                onClick={() => nav(`/packliste/${list.id}`)}
                onMouseEnter={e => e.currentTarget.style.transform='translateY(-1px)'}
                onMouseLeave={e => e.currentTarget.style.transform=''}>
                <div style={{ padding:'14px 16px' }}>
                  <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
                    <span style={{ fontSize:'1.5rem', flexShrink:0 }}>{et.emoji}</span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:700, fontSize:'.92rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {list.title}
                      </div>
                      <div style={{ fontSize:'.72rem', color:'var(--text-muted)', marginTop:2 }}>
                        {et.label}
                        {list.event_date && ` · ${new Date(list.event_date).toLocaleDateString('de-DE',{day:'2-digit',month:'short',year:'numeric'})}`}
                        {list.location && ` · ${list.location}`}
                      </div>
                      <div style={{ marginTop:6, display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                        <span className={`badge ${status.badge}`}>{status.label}</span>
                        {list.totalItems > 0 && (
                          <span style={{ fontSize:'.72rem', color:'var(--text-muted)' }}>
                            {list.packedItems || 0}/{list.totalItems} gepackt
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:5, flexShrink:0 }}>
                      <button
                        className="btn btn-ghost btn-icon btn-sm"
                        title={list.archived ? 'Wiederherstellen' : 'Archivieren'}
                        onClick={e => archiveList(list, e)}>
                        <Archive size={13}/>
                      </button>
                      {archived && (
                        <button
                          className="btn btn-danger btn-sm"
                          style={{ padding:'6px 8px' }}
                          title="Löschen"
                          onClick={e => deleteList(list, e)}>
                          <Trash2 size={13}/>
                        </button>
                      )}
                      <ChevronRight size={16} color="var(--text-faint)" style={{ marginTop:2 }}/>
                    </div>
                  </div>

                  {/* Fortschrittsbalken */}
                  {list.totalItems > 0 && (
                    <div style={{ marginTop:10 }}>
                      <div style={{ height:5, background:'var(--border)', borderRadius:999, overflow:'hidden' }}>
                        <div style={{
                          height:'100%', borderRadius:999,
                          background: pct===100 ? 'var(--green)' : pct>50 ? 'var(--yellow)' : 'var(--accent)',
                          width: `${pct}%`, transition:'width 400ms',
                        }}/>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Erstellen-Modal */}
      {modal && (
        <Modal
          title="Neue Packliste"
          onClose={() => setModal(false)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" style={{ flex:1, justifyContent:'center' }} onClick={() => setModal(false)}>
                Abbrechen
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={{ flex:1, justifyContent:'center' }}
                onClick={save}
                disabled={saving}>
                {saving ? <><span className="spinner" style={{width:14,height:14,borderWidth:2}}/> Erstellen...</> : <><Plus size={14}/> Erstellen</>}
              </button>
            </>
          }>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>

            {/* Titel */}
            <div className="f-group">
              <label className="f-label">Titel *</label>
              <input
                className="f-input"
                value={form.title}
                onChange={e => f('title', e.target.value)}
                placeholder="z.B. Schützenfest Nettetal 2026"
                autoFocus
                required
              />
            </div>

            {/* Event-Typ */}
            <div className="f-group">
              <label className="f-label">Typ</label>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:7 }}>
                {Object.entries(EVENT_TYPES).map(([v, { label, emoji }]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => f('event_type', v)}
                    style={{
                      padding:'10px 6px', borderRadius:9, border:'2px solid',
                      borderColor: form.event_type===v ? 'var(--accent)' : 'var(--border)',
                      background: form.event_type===v ? 'var(--accent-bg)' : 'var(--bg)',
                      cursor:'pointer', textAlign:'center', transition:'all 150ms',
                    }}>
                    <div style={{ fontSize:'1.3rem' }}>{emoji}</div>
                    <div style={{ fontSize:'.7rem', fontWeight:700, marginTop:3, color:form.event_type===v?'var(--accent)':'var(--text)' }}>{label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Datum + Ort */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <div className="f-group">
                <label className="f-label">Datum</label>
                <input type="date" className="f-input" value={form.event_date} onChange={e => f('event_date', e.target.value)}/>
              </div>
              <div className="f-group">
                <label className="f-label">Ort</label>
                <input className="f-input" value={form.location} onChange={e => f('location', e.target.value)} placeholder="Veranstaltungsort"/>
              </div>
            </div>

            {/* Fahrzeug */}
            {vehicles.length > 0 && (
              <div className="f-group">
                <label className="f-label">Fahrzeug</label>
                <select className="f-input" value={form.vehicle_id} onChange={e => f('vehicle_id', e.target.value)}>
                  <option value="">Kein Fahrzeug</option>
                  {vehicles.map(v => (
                    <option key={v.id} value={v.id}>{v.license_plate} — {v.brand} {v.model}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Vorlage */}
            <div style={{
              padding:'12px 14px', borderRadius:10,
              background: form.use_template ? 'var(--blue-bg)' : 'var(--bg)',
              border: `1px solid ${form.use_template ? 'rgba(37,99,235,.2)' : 'var(--border)'}`,
              transition:'all 200ms',
            }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: form.use_template && templatePreview.length > 0 ? 10 : 0 }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:'.85rem' }}>Vorlage verwenden</div>
                  <div style={{ fontSize:'.72rem', color:'var(--text-muted)', marginTop:1 }}>
                    {templatePreview.length > 0 ? `${templatePreview.length} Artikel werden hinzugefügt` : 'Keine Vorlage für diesen Typ'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => f('use_template', !form.use_template)}
                  style={{
                    width:46, height:26, borderRadius:13, border:'2px solid',
                    borderColor: form.use_template ? 'var(--blue)' : 'var(--border)',
                    background: form.use_template ? 'var(--blue)' : 'var(--bg-input)',
                    position:'relative', cursor:'pointer', transition:'all 250ms', padding:0, flexShrink:0,
                  }}>
                  <div style={{
                    position:'absolute', top:2,
                    left: form.use_template ? 22 : 2,
                    width:18, height:18, background:'#fff', borderRadius:'50%',
                    transition:'left 250ms', boxShadow:'0 1px 3px rgba(0,0,0,.25)',
                  }}/>
                </button>
              </div>

              {/* Vorschau der Vorlage-Artikel */}
              {form.use_template && templatePreview.length > 0 && (
                <div style={{ borderTop:'1px solid rgba(37,99,235,.15)', paddingTop:8 }}>
                  {Object.entries(
                    templatePreview.reduce((acc, item) => {
                      if (!acc[item.category]) acc[item.category] = [];
                      acc[item.category].push(item);
                      return acc;
                    }, {})
                  ).map(([cat, items]) => (
                    <div key={cat} style={{ marginBottom:6 }}>
                      <div style={{ fontSize:'.65rem', fontWeight:700, color:'var(--blue)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:3 }}>{cat}</div>
                      {items.map((item, i) => (
                        <div key={i} style={{ display:'flex', alignItems:'center', gap:6, fontSize:'.75rem', color:'var(--text-muted)', padding:'2px 0' }}>
                          <Check size={11} color="var(--blue)"/>
                          {item.name} × {item.quantity} {item.unit}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notizen */}
            <div className="f-group">
              <label className="f-label">Notizen</label>
              <textarea className="f-input" value={form.notes} onChange={e => f('notes', e.target.value)} rows={2} placeholder="Optionale Notizen..."/>
            </div>
          </div>
        </Modal>
      )}
    </Layout>
  );
}
